import { useCallback, useEffect, useState } from 'react';
import { TEMPLATE_NAMES } from '@game-platform/theme-constructor';
import { operatorConsole } from '../../lib/api/operatorConsole';
import { signOperatorRequest } from './signRequest';

const SESSIONS_PATH = '/loot_machine/api/game_platform/v1/sessions';

interface Props {
  /** Backend base the console (and thus this page) talks to. */
  apiBase: string;
  token: string | null;
  onToken: (token: string | null) => void;
}

interface SessionResult {
  result?: { session_token: string; expires_at: string };
  error_detail?: { code?: string; message?: string };
  message?: string;
}

const inputCls =
  'rounded border border-[#292A2D] bg-[#101112] px-2 py-1 text-[13px] text-[#F0F2F6] ' +
  'placeholder:text-[#6E727C] focus:border-[#783DF5] focus:outline-none';
const btnCls =
  'shrink-0 rounded bg-[#783DF5] px-3 py-1 text-xs font-medium text-white ' +
  'transition-colors hover:bg-[#591CDD] disabled:opacity-50';

/**
 * "Try it" session helper for the API docs page.
 *
 * Two ways to get the X-Platform-Session token the Player API needs:
 *  1. Quick preview — uses the operator console login to mint a synthetic 1h
 *     session (no secret, no get_session webhook). Auto-loaded on mount.
 *  2. Real flow — sign POST /sessions in the browser with the operator's
 *     api_key + api_secret (the exact partner flow), then use the returned
 *     session_token. Scalar can't compute the HMAC itself, so we do it here.
 *
 * Either way the resulting token is pushed up via onToken and pre-filled into
 * Scalar's auth, so every Player API request is authenticated.
 */
export function SessionPanel({ apiBase, token, onToken }: Props) {
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [playerToken, setPlayerToken] = useState('');
  const [template, setTemplate] = useState('modern');
  const [busy, setBusy] = useState<'idle' | 'preview' | 'session'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  // Seed: prefill the operator's own api_key + default template, generate a
  // player token, and auto-load a preview token so Try-It works immediately.
  useEffect(() => {
    let alive = true;
    setPlayerToken(`player-${Math.random().toString(36).slice(2, 10)}`);
    setBusy('preview');
    (async () => {
      try {
        const me = await operatorConsole.me();
        if (!alive) return;
        // Absent for team members: /me hands the api_key to the owner only,
        // and preview sessions are owner-only too, so the fields below stay
        // empty and the catch explains the rest.
        setApiKey(me.operator.api_key ?? '');
        const tpl = me.operator.default_template || 'modern';
        setTemplate(tpl);
        const res = await operatorConsole.iframeUrl({ template: tpl });
        if (!alive) return;
        onToken(res.session_token);
        setMessage('Preview token loaded — Try It is ready.');
      } catch {
        if (alive) setMessage('Could not auto-load a preview token. Sign in to the console, or create a session below.');
      } finally {
        if (alive) setBusy('idle');
      }
    })();
    return () => {
      alive = false;
    };
  }, [onToken]);

  const refreshPreview = useCallback(async () => {
    setBusy('preview');
    setMessage(null);
    try {
      const res = await operatorConsole.iframeUrl({ template: template || 'modern' });
      onToken(res.session_token);
      setMessage('Preview token loaded — Try It is ready.');
    } catch {
      setMessage('Could not get a preview token. Are you signed in to the console?');
    } finally {
      setBusy('idle');
    }
  }, [template, onToken]);

  const createSession = useCallback(async () => {
    setBusy('session');
    setMessage(null);
    try {
      const body = JSON.stringify(template ? { token: playerToken, template } : { token: playerToken });
      const signed = await signOperatorRequest({
        apiSecret,
        method: 'POST',
        fullpath: SESSIONS_PATH,
        body,
      });
      const res = await fetch(`${apiBase}${SESSIONS_PATH}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}`, ...signed },
        body,
      });
      const data = (await res.json()) as SessionResult;
      if (!res.ok || !data.result) {
        const detail = data.error_detail?.message ?? data.message ?? '';
        setMessage(`Create session failed (${res.status}) ${data.error_detail?.code ?? ''} ${detail}`.trim());
        return;
      }
      onToken(data.result.session_token);
      setMessage(`Session created — token loaded into Try It (expires ${new Date(data.result.expires_at).toLocaleString()}).`);
    } catch (err) {
      setMessage(`Error: ${err instanceof Error ? err.message : 'unknown'}`);
    } finally {
      setBusy('idle');
    }
  }, [apiBase, apiKey, apiSecret, playerToken, template, onToken]);

  return (
    <div className="border-b border-[#292A2D] bg-[#1D1E1F] px-4 py-3 text-[13px] text-[#CBD2DB]">
      <div className="flex items-center justify-between gap-3">
        <span className="font-medium text-[#F0F2F6]">Player session token — for the Player API “Try it”</span>
        <span className="text-[12px]">
          {token ? (
            <><span className="text-[#46B73C]">●</span> token active</>
          ) : (
            <><span className="text-[#6E727C]">●</span> no token</>
          )}
        </span>
      </div>

      {/* Quick preview path */}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => void refreshPreview()} disabled={busy !== 'idle'} className={btnCls}>
          {busy === 'preview' ? 'Loading…' : token ? 'Refresh preview token' : 'Get preview token'}
        </button>
        <span className="text-[12px] text-[#878B95]">No secret needed — uses your console login (valid ~1h).</span>
      </div>

      {/* Real flow path */}
      <details className="mt-2">
        <summary className="cursor-pointer text-[12px] text-[#A7ADB7] hover:text-[#F0F2F6]">
          Or create a real session with your API key + secret (the exact partner flow)
        </summary>
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-[#878B95]">API key</span>
            <input className={`${inputCls} w-48`} value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="gp_…" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-[#878B95]">API secret</span>
            <input className={`${inputCls} w-56`} type="password" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)} placeholder="kept in memory only" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-[#878B95]">Player token</span>
            <input className={`${inputCls} w-40`} value={playerToken} onChange={(e) => setPlayerToken(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-[#878B95]">Template</span>
            <select className={`${inputCls} w-28`} value={template} onChange={(e) => setTemplate(e.target.value)}>
              {TEMPLATE_NAMES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <button type="button" onClick={() => void createSession()} disabled={busy !== 'idle' || !apiKey || !apiSecret} className={btnCls}>
            {busy === 'session' ? 'Creating…' : 'Create session'}
          </button>
        </div>
        <p className="mt-1 text-[11px] text-[#6E727C]">
          Signed in your browser (HMAC-SHA256); the secret is never stored. This calls your{' '}
          <code className="text-[#A7ADB7]">get_session</code> webhook, so your callback URL must be reachable.
        </p>
      </details>

      {message && <p className="mt-2 text-[12px] text-[#CBD2DB]">{message}</p>}
    </div>
  );
}
